import { useState } from 'react';
import { useMutation } from 'react-query';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { groupsApi } from '../../../services/groups-api-client';
import {
  MAX_DINNER_SPOTS,
  buildDinnerPatch,
  capacityPrecheck,
  formFromHold,
  readDinnerEditError,
  validateDinnerForm,
  venueRenameClearsPin,
  type DinnerEditForm,
} from '../../../lib/dinnerEdit';
import type { GroupDinnerHold, UpdateDinnerHoldResponse } from '../../../types/groups';

/**
 * Change a dinner without losing the people who already claimed it.
 *
 * The form mirrors "Hold a dinner" (venue, spots, address, when, notes) and is
 * prefilled from the hold as it stood when the sheet OPENED. That snapshot is
 * what the diff runs against and what `expectedSourceVersion` comes from, so a
 * change someone else makes while this is open is a `stale` refusal, not a
 * silent overwrite. Claims and seating are untouched by design: the edit never
 * releases anybody.
 *
 * Only changed fields are sent (`buildDinnerPatch`), the time always with its
 * zone. Saving sends nobody a message — the offer to tell the guests is made by
 * the caller, after this closes.
 */
export default function EditDinnerSheet({
  groupId,
  hold,
  claimedSeats,
  zone,
  zoneIsFallback,
  onClose,
  onSaved,
  onReload,
}: {
  groupId: string;
  /** The hold as the host opened it — the baseline for the diff and the version. */
  hold: GroupDinnerHold;
  /** Seats claimed RIGHT NOW (the live list), for the capacity pre-check. */
  claimedSeats: number;
  /** The zone the time is typed and read in: the hold's, else the trip's. */
  zone: string;
  zoneIsFallback: boolean;
  onClose: () => void;
  onSaved: (result: UpdateDinnerHoldResponse) => void;
  /** Re-read the holds, then let the host open the edit again on the latest. */
  onReload: () => void;
}) {
  const [form, setForm] = useState<DinnerEditForm>(() => formFromHold(hold, zone));
  const set = (patch: Partial<DinnerEditForm>) => setForm((prev) => ({ ...prev, ...patch }));

  const mutation = useMutation(
    (body: Parameters<typeof groupsApi.updateDinnerHold>[2]) => groupsApi.updateDinnerHold(groupId, hold.id, body),
    { onSuccess: onSaved },
  );
  const failure = mutation.error ? readDinnerEditError(mutation.error) : null;

  const errors = validateDinnerForm(form, zone);
  const patch = buildDinnerPatch(hold, form, zone);
  const precheck = capacityPrecheck(hold, claimedSeats, Number.parseInt(form.totalSpots, 10));
  // Both refusals that a retry cannot fix: the dinner is gone, or it moved on.
  const dead = failure?.kind === 'stale' || failure?.kind === 'hold_cancelled';
  const canSave = Boolean(patch) && Object.keys(errors).length === 0 && precheck.ok
    && !mutation.isLoading && !dead;

  const handleSave = () => {
    if (!canSave || !patch) return;
    mutation.mutate(patch.body);
  };

  return (
    <Dialog open onClose={mutation.isLoading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit dinner</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Guests keep their seats. Changing the time, venue or notes never releases anyone — and
          nobody is messaged until you say so.
        </Alert>
        {failure ? (
          <Alert
            severity={failure.kind === 'stale' ? 'warning' : 'error'}
            sx={{ mb: 2 }}
            action={failure.kind === 'stale' ? (
              <Button color="inherit" size="small" onClick={onReload}>Reload</Button>
            ) : undefined}
          >
            {failure.message}
          </Alert>
        ) : null}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth label="Restaurant / venue" value={form.venueName}
              onChange={(e) => set({ venueName: e.target.value.slice(0, 500) })}
              error={Boolean(errors.venueName)}
              helperText={errors.venueName
                ?? (venueRenameClearsPin(hold, form)
                  ? 'A different venue removes the old map pin, so guests are never pointed at the wrong place.'
                  : undefined)}
            />
            <TextField
              label="Total spots" type="number" value={form.totalSpots}
              onChange={(e) => set({ totalSpots: e.target.value })}
              inputProps={{ min: 1, max: MAX_DINNER_SPOTS }} sx={{ maxWidth: { sm: 140 } }}
              error={Boolean(errors.totalSpots) || !precheck.ok}
              helperText={errors.totalSpots ?? undefined}
            />
          </Stack>
          {/* Read out loud, in the field's own words, before the host can press Save. */}
          {!precheck.ok ? <Alert severity="warning">{precheck.message}</Alert> : null}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth label="Address (optional)" value={form.address}
              onChange={(e) => set({ address: e.target.value })}
            />
            <TextField
              label={`When (${zone})`} type="datetime-local" value={form.whenLocal}
              onChange={(e) => set({ whenLocal: e.target.value })}
              error={Boolean(errors.when)}
              helperText={errors.when
                ?? (zoneIsFallback ? 'This trip has no time zone set — using yours.' : 'Local time at the dinner.')}
              InputLabelProps={{ shrink: true }} sx={{ maxWidth: { sm: 260 } }}
            />
          </Stack>
          <TextField
            fullWidth multiline minRows={2} label="Notes for guests (optional)" value={form.notes}
            onChange={(e) => set({ notes: e.target.value })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isLoading}>Cancel</Button>
        <Button variant="contained" disabled={!canSave} onClick={handleSave}>
          {mutation.isLoading ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
